import axios from 'axios';
import env from '../utils/env';
import { addToQueue } from '../utils/offlineQueue';
import localDB from './localData';

// Create axios instance with increased timeout
const api = axios.create({
  baseURL: env.REACT_APP_API_BASE_URL,
  timeout: 30000, // Increased from 5000ms to 30000ms (30 seconds)
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Retry utility function
const retryRequest = async (requestFn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Only retry on timeout or network errors
      if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        if (attempt < maxRetries - 1) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = baseDelay * Math.pow(2, attempt);
          console.log(`Request failed (attempt ${attempt + 1}/${maxRetries}), retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      } else {
        // Don't retry on other errors (4xx, 5xx, etc.)
        throw error;
      }
    }
  }
  
  throw lastError;
};

// Enhanced handleOfflineRequest with retry logic
const handleOfflineRequest = async (request, entityType) => {
  if (navigator.onLine) {
    try {
      // Add retry logic for online requests
      const response = await retryRequest(() => api(request));
      return response;
    } catch (error) {
      console.error('API request failed after retries:', error);
      throw error;
    }
  } else {
    // Generate a temporary ID for new entities
    const tempId = `temp_${Date.now()}`;
    
    const enhancedRequest = {
      ...request,
      entityType,
      metadata: {
        entityType,
        tempId,
        timestamp: new Date().toISOString(),
        ...(request.metadata || {})
      }
    };

    await addToQueue(enhancedRequest);
    
    if (request.method === 'POST') {
      try {
        await localDB[entityType].add({
          ...request.data,
          id: tempId,
          syncStatus: 'pending'
        });
      } catch (error) {
        console.error('Failed to save locally:', error);
      }
    }
    
    throw new Error('Queued for sync when online');
  }
};

const createOfflineHandler = (method, url, entityType) => async (data) => {
  try {
    return await handleOfflineRequest({
      url,
      method,
      data
    }, entityType);
  } catch (error) {
    if (error.message === 'Queued for sync when online') {
      if (method === 'PUT' && data.id) {
        await localDB[entityType].update(data.id, {
          ...data,
          syncStatus: 'pending'
        });
      } else if (method === 'DELETE' && data.id) {
        await localDB[entityType].delete(data.id);
      }
      
      return { data: { success: true, message: 'Operation queued for sync' } };
    }
    throw error;
  }
};

// Enhanced attendanceAPI with retry for specific methods
export const attendanceAPI = {
  getStudents: (params) => {
    const token = localStorage.getItem('token');
    return retryRequest(() => 
      api.get('/attendance/students', { 
        params, 
        timeout: 30000,
        headers: { Authorization: `Bearer ${token}` }
      })
    );
  },
  
  getRecords: (params) => {
    const token = localStorage.getItem('token');
    return retryRequest(() => 
      api.get('/attendance/records', { 
        params, 
        timeout: 30000,
        headers: { Authorization: `Bearer ${token}` }
      })
    );
  },
  
  submitRecords: (data) => {
    const token = localStorage.getItem('token');
    return handleOfflineRequest({
      url: '/attendance/records',
      method: 'POST',
      data,
      headers: { Authorization: `Bearer ${token}` },
      metadata: {
        entityType: 'attendance',
        tempId: `temp_${Date.now()}`
      }
    }, 'attendance');
  },
  
  deleteRecords: (ids) => {
    const token = localStorage.getItem('token');
    return handleOfflineRequest({
      url: '/attendance/records',
      method: 'DELETE',
      data: { ids },
      headers: { Authorization: `Bearer ${token}` },
      metadata: {
        entityType: 'attendance'
      }
    }, 'attendance');
  },

  // ============= NEW QR METHODS =============
  
  // Generate QR code (Staff/Admin only)
  generateQR: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/attendance/generate-qr', data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Scan QR code (Student only)
  scanQR: (qrCode) => {
    const token = localStorage.getItem('token');
    return api.post('/attendance/scan-qr', { qrCode }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Get active QR codes (Staff/Admin only)
  getActiveQRs: () => {
    const token = localStorage.getItem('token');
    return api.get('/attendance/active-qrs', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Check if student marked attendance today
  getStudentTodayAttendance: (studentId) => {
    const token = localStorage.getItem('token');
    return api.get(`/attendance/student-today/${studentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  getTodaySummary: () => retryRequest(() =>
  api.get('/attendance/today-summary', { timeout: 30000 })
),
};

// Enhanced schoolFeeAPI with retry
export const schoolFeeAPI = {
  getStudents: ({ page = 1, limit = 75, name, class: className, year }) => 
    retryRequest(() => api.get('/schoolfee/students', {
      params: { page, limit, name: name || undefined, class: className || undefined, year },
      timeout: 30000
    })),
  
 getRecords: ({ page = 1, limit = 75, name, class: className, sortField, sortDirection, year }) => 
  retryRequest(() => api.get('/schoolfee/records', {
    params: { page, limit, name: name || undefined, class: className || undefined, sortField, sortDirection, year },
    timeout: 30000
  })),
    
  getPaymentHistory: (studentId) => retryRequest(() => 
    api.get(`/schoolfee/payment-history/${studentId}`, { timeout: 30000 })
  ),

  getFeeAmounts: (params = {}) => retryRequest(() => 
  api.get('/schoolfee/amounts', { params, timeout: 30000 })
),
  
  createFeeAmount: (data) => handleOfflineRequest({
    url: '/schoolfee/amounts',
    method: 'POST',
    data,
    metadata: {
      entityType: 'feeAmount',
      tempId: `temp_${Date.now()}`
    }
  }, 'feeAmount'),
  
  updateFeeAmount: (id, data) => handleOfflineRequest({
    url: `/schoolfee/amounts/${id}`,
    method: 'PUT',
    data: { id, ...data },
    metadata: {
      entityType: 'feeAmount'
    }
  }, 'feeAmount'),
  
  deleteFeeAmount: (id) => handleOfflineRequest({
    url: `/schoolfee/amounts/${id}`,
    method: 'DELETE',
    data: { id },
    metadata: {
      entityType: 'feeAmount'
    }
  }, 'feeAmount'),

  submitRecords: (data) => handleOfflineRequest({
    url: '/schoolfee/submit',
    method: 'POST',
    data,
    metadata: {
      entityType: 'fee',
      tempId: `temp_${Date.now()}`
    }
  }, 'fee'),
  
  getTotals: (params = {}) => retryRequest(() => 
  api.get('/schoolfee/totals', { params, timeout: 30000 })
),
  
  updateSchoolFee: (id, data) => handleOfflineRequest({
    url: `/schoolfee/records/update/${id}`,
    method: 'PUT',
    data: { id, ...data },
    metadata: {
      entityType: 'fee'
    }
  }, 'fee'),
  
  updateMiscFee: (id, data) => handleOfflineRequest({
    url: `/schoolfee/records/misc/update/${id}`,
    method: 'PUT',
    data: { id, ...data },
    metadata: {
      entityType: 'fee'
    }
  }, 'fee'),
  
  deleteSchoolFee: (id) => handleOfflineRequest({
    url: `/schoolfee/records/delete/${id}`,
    method: 'DELETE',
    data: { id },
    metadata: {
      entityType: 'fee'
    }
  }, 'fee'),
  
  deleteMiscFee: (id) => handleOfflineRequest({
    url: `/schoolfee/records/misc/delete/${id}`,
    method: 'DELETE',
    data: { id },
    metadata: {
      entityType: 'fee'
    }
  }, 'fee'),

 getPaymentStatusSummary: (params = {}) => retryRequest(() => 
  api.get('/schoolfee/payment-status-summary', { params, timeout: 30000 })
),
getMiscFeeTypeSummary: (params = {}) => retryRequest(() => 
  api.get('/schoolfee/misc-fee-type-summary', { params, timeout: 30000 })
),
getRefundSummary: () => retryRequest(() =>
  api.get('/schoolfee/refund-summary', { timeout: 30000 })
)
};

// Enhanced studentAPI with retry
export const studentAPI = {
  getStudents: ({ page = 1, limit = 75, search = '', sortBy = 'studentName', sortOrder = 'asc' }) => 
    retryRequest(() => api.get('/students', {
      params: { page, limit, search, sortBy, sortOrder },
      paramsSerializer: { indexes: null, encode: (param) => encodeURIComponent(param) },
      timeout: 30000
    })),
  
  addStudent: (data) => handleOfflineRequest({
    url: '/students',
    method: 'POST',
    data,
    metadata: {
      entityType: 'student',
      tempId: `temp_${Date.now()}`
    }
  }, 'student'),
  
  updateStudent: (id, data) => handleOfflineRequest({
    url: `/students/${id}`,
    method: 'PUT',
    data: { id, ...data },
    metadata: {
      entityType: 'student'
    }
  }, 'student'),
  
  deleteStudent: (id) => handleOfflineRequest({
    url: `/students/${id}`,
    method: 'DELETE',
    data: { id },
    metadata: {
      entityType: 'student'
    }
  }, 'student'),

  patch: (id, data) => retryRequest(() =>
  api.patch(`/students/${id}`, data, { timeout: 30000 })
),
  
  getStatistics: () => retryRequest(() => 
    api.get('/students/stats', { timeout: 30000 })
  ),
  
  getExportData: () => retryRequest(() => 
    api.get('/students/export', { timeout: 60000 }) // Longer timeout for export
  ),

  getStudentsByClass: (className) => retryRequest(() => 
    api.get(`/students/class/${className}`, { timeout: 30000 })
  ),

  getStudentsByConstituency: (constituencyName) => retryRequest(() => 
    api.get(`/students/constituency/${encodeURIComponent(constituencyName)}`, { timeout: 30000 })
  ),
  
  getStudentsByRegion: (regionName) => retryRequest(() => 
    api.get(`/students/region/${encodeURIComponent(regionName)}`, { timeout: 30000 })
  ),
  
  getStudentsByReligion: (religionName) => retryRequest(() => 
    api.get(`/students/religion/${encodeURIComponent(religionName)}`, { timeout: 30000 })
  ),

  //  fetches the next auto-generated Student ID from the backend
  generateStudentId: () => retryRequest(() => 
    api.get('/students/generate-id', { timeout: 30000 })
  ),

  getRecentEnrollments: () => retryRequest(() =>
  api.get('/students', {
    params: {
      sortBy: 'registrationDate',
      sortOrder: 'DESC',
      limit: 5,
      page: 1
    },
    timeout: 30000
  })
),

  changePassword: (data) => {
  const token = localStorage.getItem('token');
  return retryRequest(() => api.put('/admin/student/change-password', data, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000
  }));
},
};

// Rest of your API exports remain the same...
export const staffAPI = {
  getStaffs: () => retryRequest(() => api.get('/getStaffs', { timeout: 30000 })),
  addStaff: (data) => handleOfflineRequest({
    url: '/addStaff',
    method: 'POST',
    data
  }, 'staff'),
  updateStaff: (id, data) => handleOfflineRequest({
    url: `/updateStaff/${id}`,
    method: 'PUT',
    data: { id, ...data }
  }, 'staff'),
  deleteStaff: (id) => handleOfflineRequest({
    url: `/deleteStaff/${id}`,
    method: 'DELETE',
    data: { id }
  }, 'staff')
};

export const gradesAPI = {
  getSubjects: () => retryRequest(() => api.get('/subjects/simple-list', { timeout: 30000 })),
  getStudents: ({ page = 1, limit = 75, search = '', classFilter = null, sortBy = 'studentName', sortOrder = 'asc' }) => 
    retryRequest(() => api.get('/grades/students', {
      params: { page, limit, search, classFilter, sortBy, sortOrder },
      timeout: 30000
    })),
  getGrades: ({ page = 1, limit = 75, search = '', classFilter = null, sortBy = 'studentName', sortOrder = 'asc' }) => 
    retryRequest(() => api.get('/grades/grades', {
      params: { page, limit, search, classFilter, sortBy, sortOrder },
      timeout: 30000
    })),
  getStudentGrades: (studentId) => retryRequest(() => 
    api.get(`/grades/student/${studentId}`, { timeout: 30000 })
  ),
  saveGrades: (data) => handleOfflineRequest({
    url: '/grades',
    method: 'POST',
    data
  }),
  updateGrade: (id, data) => handleOfflineRequest({
    url: `/grades/grades/${id}`,
    method: 'PUT',
    data
  }),
  deleteGrade: (id) => handleOfflineRequest({
    url: `/grades/grades/${id}`,
    method: 'DELETE'
  })
};

export const learningMaterialsAPI = {
  getFiles: (token) => retryRequest(() => api.get('/files', {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000
  })),
  
  uploadFile: (formData, onUploadProgress, token) => handleOfflineRequest({
    url: '/upload',
    method: 'POST',
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`
    },
    onUploadProgress,
    timeout: 1800000
  }, 'file'),
  
  downloadFile: (filename, token) => api.get(`/download/${filename}`, {
    responseType: "blob",
    headers: { Authorization: `Bearer ${token}` },
    timeout: 1800000
  }),
  
  deleteFile: (filename, token) => handleOfflineRequest({
    url: `/delete/${filename}`,
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` }
  }),
  
  getStudentFeeStatus: (studentId, token) => retryRequest(() => api.get(`/schoolfee/student/${studentId}/status`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000
  })),

  getSummary: (token) => retryRequest(() =>
  api.get('/files/summary', {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000
  })
),
};

export const communicationAPI = {
  getAnnouncements: () => retryRequest(() => api.get('/announcements', { timeout: 30000 })),
  createAnnouncement: (data) => handleOfflineRequest({
    url: '/announcements',
    method: 'POST',
    data
  }),
  updateAnnouncement: (id, data) => handleOfflineRequest({
    url: `/announcements/${id}`,
    method: 'PUT',
    data
  }),
  deleteAnnouncement: (id) => handleOfflineRequest({
    url: `/announcements/${id}`,
    method: 'DELETE'
  })
};

export const dashboardAPI = {
  getStudentsCount: () => retryRequest(() => api.get('/students/count', { timeout: 30000 })),
  getStaffsCount: () => retryRequest(() => api.get('/getStaffs', { timeout: 30000 })),
  getSubjectsCount: () => retryRequest(() => api.get('/subjects/simple-list', { timeout: 30000 }))
};

export const subjectsAPI = {
  getSubjects: () => retryRequest(() => api.get('/subjects', { timeout: 30000 })),
  createClass: (data) => handleOfflineRequest({
    url: '/classes',
    method: 'POST',
    data
  }),
  updateClass: (id, data) => handleOfflineRequest({
    url: `/classes/${id}`,
    method: 'PUT',
    data
  }),
  deleteClass: (id) => handleOfflineRequest({
    url: `/classes/${id}`,
    method: 'DELETE'
  }),
  createStream: (data) => handleOfflineRequest({
    url: '/streams',
    method: 'POST',
    data
  }),
  updateStream: (id, data) => handleOfflineRequest({
    url: `/streams/${id}`,
    method: 'PUT',
    data
  }),
  deleteStream: (id) => handleOfflineRequest({
    url: `/streams/${id}`,
    method: 'DELETE'
  }),
  createSubject: (data) => handleOfflineRequest({
    url: '/subjects',
    method: 'POST',
    data
  }),
  updateSubject: (id, data) => handleOfflineRequest({
    url: `/subjects/${id}`,
    method: 'PUT',
    data
  }),
  deleteSubject: (id) => handleOfflineRequest({
    url: `/subjects/${id}`,
    method: 'DELETE'
  })
};

export const adminAPI = {
  getAdminInfo: (token) => retryRequest(() => api.get('/admin/info', {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000
  })),
  updateProfile: (token, data) => handleOfflineRequest({
    url: '/admin/update-profile',
    method: 'PUT',
    data,
    headers: { Authorization: `Bearer ${token}` }
  }),
  changePassword: (token, data) => retryRequest(() => api.post('/admin/change-password', data, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000
  }))
};

export const authAPI = {
  adminSignUp: (data) => retryRequest(() => api.post('/admin/signup', data, { timeout: 30000 })),
  adminSignIn: (data) => retryRequest(() => api.post('/admin/signin', data, { withCredentials: true, timeout: 30000 })),
  staffSignIn: (data) => retryRequest(() => api.post('/admin/staff/signin', data, { withCredentials: true, timeout: 30000 })),
  studentSignIn: (data) => retryRequest(() => api.post('/admin/student/signin', data, { withCredentials: true, timeout: 30000 })),
  forgotPassword: (data) => retryRequest(() => api.post('/admin/forgot-password', data, { timeout: 30000 })),
  validateToken: (token) => retryRequest(() => api.post('/admin/validateToken', null, {
    headers: { Authorization: token },
    timeout: 30000
  }))
};

export const dynamicOptionsAPI = {
  getOptions: (fieldName) =>
    retryRequest(() => api.get(`/students/field-options/${fieldName}`, { timeout: 30000 })),

  saveOption: (fieldName, optionValue) =>
    api.post('/students/field-options', { fieldName, optionValue }, { timeout: 30000 }),
};